import { isValidTime } from './types';
import type { DocketProject, DocketSession, ProjectUpdate, RemainingScopeItem, WaitKind } from './types';

export type CloseResult = { readonly ok: true } | { readonly ok: false; readonly reasons: readonly string[] };

export interface CloseContext {
  /** The session being closed: it stops owning anything the moment this close lands. */
  readonly closingSessionId: string;
  readonly nowIso: string;
  /** The Nick-item ids that exist (NICK-DESK / decisions). When given, a Nick owner must be in it. */
  readonly nickItemIds?: ReadonlySet<string>;
}

const DEAD = new Set(['DONE', 'DROPPED']);
const STATES = new Set(['OPEN', 'DONE', 'DROPPED']);
const WAIT_KINDS = new Set<WaitKind>(['nick', 'time', 'pr', 'row']);
const nonBlank = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

/** Why an OPEN scope item has no live owner, or null when it has one. */
export function scopeOwnerProblem(
  item: RemainingScopeItem,
  sessions: readonly DocketSession[],
  projectId: string,
  opts: { readonly closingSessionId?: string; readonly nickItemIds?: ReadonlySet<string> } = {},
): string | null {
  if (!STATES.has(item.state)) return `remaining scope "${item.item}" has unknown state "${String(item.state)}"`;
  if (item.state !== 'OPEN') return null;
  const hasNick = item.nickItemId !== undefined;
  if (hasNick) {
    if (!nonBlank(item.nickItemId)) return `remaining scope "${item.item}" has a blank Nick item id`;
    if (opts.nickItemIds && !opts.nickItemIds.has(item.nickItemId)) {
      return `remaining scope "${item.item}" points at Nick item ${item.nickItemId}, which does not exist`;
    }
    if (item.sessionId === undefined) return null;
  }
  // A session id, when present, must be live whether or not a Nick item is also named.
  if (!nonBlank(item.sessionId)) return `remaining scope "${item.item}" has no session or Nick item`;
  if (item.sessionId === opts.closingSessionId) return `remaining scope "${item.item}" is owned by the session being closed`;
  const s = sessions.find((x) => x.id === item.sessionId);
  if (!s) return `remaining scope "${item.item}" points at session ${item.sessionId}, which does not exist`;
  if (DEAD.has(s.status)) return `remaining scope "${item.item}" points at session ${item.sessionId}, which is ${s.status}`;
  if (s.projectId !== projectId) return `remaining scope "${item.item}" points at session ${item.sessionId} of another project`;
  return null;
}

/** Closing a session must leave the project's state durable; a close that cannot is refused. */
export function validateClose(
  project: DocketProject,
  sessions: readonly DocketSession[],
  update: ProjectUpdate,
  ctx: CloseContext,
): CloseResult {
  const reasons: string[] = [];
  const hasUpdate = (update.built?.some(nonBlank) ?? false) || update.liveVerified != null
    || (update.remainingScope?.length ?? 0) > 0 || (update.waitsOn?.length ?? 0) > 0;
  if (!hasUpdate && !nonBlank(update.unchangedReason)) {
    reasons.push('give a project update (built / liveVerified / remainingScope / waitsOn) or an unchangedReason');
  }
  if (!isValidTime(ctx.nowIso)) reasons.push(`close time "${String(ctx.nowIso)}" is not a date-time`);
  const lv = update.liveVerified;
  if (lv) {
    if (!Number.isInteger(lv.n) || !Number.isInteger(lv.of) || lv.of < 1 || lv.n < 0 || lv.n > lv.of) {
      reasons.push(`liveVerified ${String(lv.n)} of ${String(lv.of)} cannot be true`);
    }
    if (!nonBlank(lv.control)) reasons.push('liveVerified needs a named control');
    if (!isValidTime(lv.readAt)) reasons.push('liveVerified needs a readAt date-time');
    else if (Date.parse(lv.readAt) > Date.parse(ctx.nowIso)) reasons.push('liveVerified readAt is in the future');
  }
  for (const w of update.waitsOn ?? []) {
    if (!WAIT_KINDS.has(w.kind) || !nonBlank(w.ref)) reasons.push(`waitsOn entry ${JSON.stringify(w)} needs a known kind and a ref`);
  }
  const dupes = (items: readonly RemainingScopeItem[]): string[] => {
    const seen = new Set<string>();
    return items.flatMap((i) => (seen.has(i.item) ? [i.item] : (seen.add(i.item), [])));
  };
  for (const d of dupes(update.remainingScope ?? project.remainingScope)) {
    reasons.push(`scope item text "${d}" appears twice: items are matched by text, so make each one unique`);
  }
  if (update.remainingScope) {
    const kept = new Set(update.remainingScope.map((i) => i.item));
    for (const old of project.remainingScope) {
      if (old.state === 'OPEN' && !kept.has(old.item)) {
        reasons.push(`open scope "${old.item}" was removed: mark it DONE or DROPPED instead`);
      }
    }
  }
  for (const item of update.remainingScope ?? project.remainingScope) {
    const p = scopeOwnerProblem(item, sessions, project.id, { closingSessionId: ctx.closingSessionId, nickItemIds: ctx.nickItemIds });
    if (p) reasons.push(p);
  }
  return reasons.length ? { ok: false, reasons } : { ok: true };
}
